import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { 
  ChartBarIcon, 
  CurrencyDollarIcon,
  DocumentTextIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  LockClosedIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import CompanyLogo from '../components/common/CompanyLogo';
import TickerSearchInput from '../components/common/TickerSearchInput';

interface Deal {
  id: string;
  companyName: string;
  symbol: string;
  value: number;
  fee: number;
  daysInStage: number;
  exclusivityType?: string;
}

interface Pipeline {
  prospecting: Deal[];
  termSheetSent: Deal[];
  dueDiligence: Deal[];
  closing: Deal[];
  completed: Deal[];
}

interface BrokerMetrics {
  activeDeals: number;
  completedDeals: number;
  totalCommissions: number;
  averageDealSize: number;
}

const BrokerDashboard: React.FC = () => {
  const [pipeline, setPipeline] = useState<Pipeline>({
    prospecting: [],
    termSheetSent: [],
    dueDiligence: [],
    closing: [],
    completed: []
  });
  const [metrics, setMetrics] = useState<BrokerMetrics | null>(null);
  const [showNewDeal, setShowNewDeal] = useState(false);
  const [searchSymbol, setSearchSymbol] = useState('');
  const [exclusivityCheck, setExclusivityCheck] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    fetchPipeline();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get('/api/broker/dashboard');
      setMetrics(response.data.metrics);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const fetchPipeline = async () => {
    try {
      const response = await axios.get('/api/broker/pipeline');
      setPipeline(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching pipeline:', error);
      setLoading(false);
    }
  };

  const checkExclusivity = async () => {
    if (!searchSymbol) return;
    
    try {
      const response = await axios.get(`/api/broker/exclusivity/${searchSymbol}`);
      setExclusivityCheck(response.data);
    } catch (error) {
      console.error('Error checking exclusivity:', error);
    }
  };

  const onDragEnd = async (result: any) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;
    
    if (source.droppableId === destination.droppableId) {
      // Reordering within same column
      const column = [...pipeline[source.droppableId as keyof Pipeline]];
      const [removed] = column.splice(source.index, 1);
      column.splice(destination.index, 0, removed);
      
      setPipeline({
        ...pipeline,
        [source.droppableId]: column
      });
    } else {
      // Moving between columns
      const sourceColumn = [...pipeline[source.droppableId as keyof Pipeline]];
      const destColumn = [...pipeline[destination.droppableId as keyof Pipeline]];
      const [removed] = sourceColumn.splice(source.index, 1);
      destColumn.splice(destination.index, 0, removed);
      
      setPipeline({
        ...pipeline,
        [source.droppableId]: sourceColumn,
        [destination.droppableId]: destColumn
      });

      // Update backend
      try {
        await axios.put(`/api/broker/deal/${draggableId}/stage`, {
          newStage: destination.droppableId
        });
      } catch (error) {
        console.error('Error updating deal stage:', error);
        // Revert on error
        fetchPipeline();
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStageColor = (stage: string) => {
    const colors: { [key: string]: string } = {
      prospecting: 'bg-gray-100',
      termSheetSent: 'bg-blue-100',
      dueDiligence: 'bg-yellow-100',
      closing: 'bg-green-100',
      completed: 'bg-purple-100'
    };
    return colors[stage] || 'bg-gray-100';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Broker Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage your deals and track commissions</p>
        </div>

        {/* Metrics */}
        {metrics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Deals</p>
                  <p className="text-2xl font-bold text-gray-900">{metrics.activeDeals}</p>
                </div>
                <ChartBarIcon className="h-8 w-8 text-blue-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Completed Deals</p>
                  <p className="text-2xl font-bold text-gray-900">{metrics.completedDeals}</p>
                </div>
                <DocumentTextIcon className="h-8 w-8 text-green-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Commissions</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(metrics.totalCommissions)}
                  </p>
                </div>
                <CurrencyDollarIcon className="h-8 w-8 text-yellow-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avg Deal Size</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(metrics.averageDealSize)}
                  </p>
                </div>
                <UserGroupIcon className="h-8 w-8 text-purple-500" />
              </div>
            </motion.div>
          </div>
        )}

        {/* New Deal Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">New Deal Inquiry</h2>
            <button
              onClick={() => setShowNewDeal(!showNewDeal)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              New Deal
            </button>
          </div>

          {showNewDeal && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4"
            >
              <div className="flex gap-4">
                <div className="flex-1">
                  <TickerSearchInput
                    value={searchSymbol}
                    onChange={(value) => setSearchSymbol(value)}
                    onSelect={(option) => {
                      setSearchSymbol(option.symbol);
                      // Store company name for later use
                      sessionStorage.setItem(`ticker_${option.symbol}_name`, option.name);
                    }}
                    placeholder="Search by company name or ticker symbol"
                    showPopularOnFocus={true}
                  />
                </div>
                <button
                  onClick={checkExclusivity}
                  className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
                >
                  <MagnifyingGlassIcon className="h-5 w-5" />
                  Check Exclusivity
                </button>
              </div>

              {exclusivityCheck && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4"
                >
                  {exclusivityCheck.hasExclusivity ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CompanyLogo 
                          ticker={searchSymbol} 
                          companyName={exclusivityCheck.companyName} 
                          size="md" 
                        />
                        <div className="flex items-start gap-3 flex-1">
                          <LockClosedIcon className="h-5 w-5 text-red-600 mt-0.5" />
                          <div>
                            <p className="font-semibold text-red-900">Exclusivity Active</p>
                            <p className="text-red-700 mt-1">
                              This company is under {exclusivityCheck.exclusivityType} exclusivity with {exclusivityCheck.brokerFirm}
                            </p>
                            <p className="text-red-600 text-sm mt-2">
                              Expires: {new Date(exclusivityCheck.expiresAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : exclusivityCheck.isOwnExclusivity ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CompanyLogo 
                          ticker={searchSymbol} 
                          companyName={exclusivityCheck.companyName} 
                          size="md" 
                        />
                        <div className="flex items-start gap-3 flex-1">
                          <ExclamationTriangleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                          <div>
                            <p className="font-semibold text-blue-900">Existing Inquiry</p>
                            <p className="text-blue-700 mt-1">
                              You already have an active inquiry for this company
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CompanyLogo 
                            ticker={searchSymbol} 
                            companyName={exclusivityCheck.companyName} 
                            size="md" 
                          />
                          <div>
                            <p className="font-semibold text-green-900">Available</p>
                            <p className="text-green-700 mt-1">
                              This symbol is available for inquiry
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => window.location.href = `/broker/eligibility/${searchSymbol}`}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Run Eligibility Check
                        </button>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          )}
        </div>

        {/* Deal Pipeline */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold mb-6">Deal Pipeline</h2>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <div className="grid grid-cols-5 gap-4">
                {Object.entries(pipeline).map(([stage, deals]) => (
                  <div key={stage} className={`${getStageColor(stage)} rounded-lg p-4`}>
                    <h3 className="font-semibold text-gray-900 mb-3 capitalize">
                      {stage.replace(/([A-Z])/g, ' $1').trim()}
                    </h3>
                    <Droppable droppableId={stage}>
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="space-y-2 min-h-[200px]"
                        >
                          {deals.map((deal: Deal, index: number) => (
                            <Draggable key={deal.id} draggableId={deal.id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`bg-white p-3 rounded-lg shadow-sm border border-gray-200 cursor-move
                                    ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                                >
                                  <div className="flex items-center gap-2 mb-2">
                                    <CompanyLogo 
                                      ticker={deal.symbol} 
                                      companyName={deal.companyName} 
                                      size="sm" 
                                    />
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between">
                                        <p className="font-semibold text-sm">{deal.symbol}</p>
                                        <span className="text-xs text-gray-500">{deal.daysInStage}d</span>
                                      </div>
                                      <p className="text-xs text-gray-600">{deal.companyName}</p>
                                    </div>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <p className="text-sm font-medium">{formatCurrency(deal.value)}</p>
                                    <p className="text-xs text-green-600">{(deal.fee * 100).toFixed(2)}%</p>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                ))}
              </div>
            </DragDropContext>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrokerDashboard; 